// Operation Health — frontend presenter (Step 17G).
//
// Single, centralized, pure mapping from the backend `operation_health`
// payload (services/operations/health/operationHealth.js +
// operationHealthAdapter.js in wine2trace-server, contract version "V1") to a
// stable UI model. Components must never re-derive status/severity/reason
// codes themselves, must never show raw payload keys to the user, and must
// never combine the backend result with legacy client-side checks.
//
// Contract recap (read-only reference, not reproduced/re-implemented here):
//   { operation_id, applicable, health_status, severity, issue_count,
//     blocking_issue_count, issues: [{code, severity, blocking, entity_type,
//     entity_id, params}], validated_at, validation_contract_version,
//     lifecycle_classification? }
//
// `lifecycle_classification` is attached by the backend only when
// applicable=false (classifyLifecycle in wine2trace-server's
// operationHealth.js). COMPLETED_SNAPSHOT / CANCELLED /
// CANCELLED_HISTORICAL_SNAPSHOT_WITH_COMPLETION_DATE mean the operation is a
// finalized/cancelled historical fact: the live-state checks (src tank now
// empty, quantity sufficiency, time overlap) are meaningless for it, so it is
// surfaced as NOT_APPLICABLE with no list-badge noise. Persisting the
// historical outcome of the checks as of completion time — so a completed
// operation could show a genuine past VALID/WARNING/BLOCKED result instead of
// NOT_APPLICABLE — is a deferred follow-up, not implemented here.
//
// This module intentionally does NOT depend on i18n (`getLabel`) — it only
// returns stable i18n *keys* (titleKey/descriptionKey/reasonItems[].labelKey).
// Resolving those keys into localized text is the UI layer's job, so this
// presenter stays pure and trivially unit-testable.

export const OPERATION_HEALTH_STATUS = Object.freeze({
  VALID: "VALID",
  WARNING: "WARNING",
  BLOCKED: "BLOCKED",
  STALE: "STALE",
  UNKNOWN: "UNKNOWN",
  NOT_APPLICABLE: "NOT_APPLICABLE",
});

const SUPPORTED_CONTRACT_VERSION = "V1";

// Backend status -> UI keys/severity. Precedence between statuses is decided
// exclusively by the backend engine (Step 17C); this table only assigns a
// presentation, it never re-ranks or recomputes anything.
const STATUS_PRESENTATION = Object.freeze({
  VALID: {
    titleKey: "operationHealthStatusValidTitle",
    descriptionKey: "operationHealthStatusValidDescription",
    severity: "positive",
  },
  WARNING: {
    titleKey: "operationHealthStatusWarningTitle",
    descriptionKey: "operationHealthStatusWarningDescription",
    severity: "warning",
  },
  BLOCKED: {
    titleKey: "operationHealthStatusBlockedTitle",
    descriptionKey: "operationHealthStatusBlockedDescription",
    severity: "error",
  },
  STALE: {
    titleKey: "operationHealthStatusStaleTitle",
    descriptionKey: "operationHealthStatusStaleDescription",
    severity: "conservative",
  },
  UNKNOWN: {
    titleKey: "operationHealthStatusUnknownTitle",
    descriptionKey: "operationHealthStatusUnknownDescription",
    severity: "conservative",
  },
  NOT_APPLICABLE: {
    titleKey: "operationHealthStatusNotApplicableTitle",
    descriptionKey: "operationHealthStatusNotApplicableDescription",
    severity: "neutral",
  },
});

// V1 reason code catalog -> stable i18n key. Any code outside this set is a
// reason code we don't (yet) know how to translate — it must degrade to the
// generic "unknown reason" key, never be shown as a raw code, and never be
// silently dropped (the raw code is preserved internally, see buildReasonItems).
const REASON_LABEL_KEYS = Object.freeze({
  SRC_TANK_NOW_EMPTY: "operationHealthReasonSrcTankNowEmpty",
  SRC_QUANTITY_INSUFFICIENT: "operationHealthReasonSrcQuantityInsufficient",
  TIME_OVERLAP: "operationHealthReasonTimeOverlap",
});

const UNKNOWN_REASON_LABEL_KEY = "operationHealthReasonUnknown";

function isPlainObject(value) {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function toValidDate(value) {
  if (value === null || value === undefined) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

// Maps the backend's `issues[]` into a UI-ready, order-preserving list.
// Order is never recomputed here: the backend already returns issues in its
// own canonical, deduplicated order (services/operations/health/
// operationHealth.js `aggregateCanonicalIssues`), and the frontend has no
// canonical order of its own to impose.
function buildReasonItems(issues) {
  return issues.map((issue, index) => {
    const code = isPlainObject(issue) && typeof issue.code === "string" ? issue.code : null;
    const isKnown = code !== null && Object.prototype.hasOwnProperty.call(REASON_LABEL_KEYS, code);
    return {
      code,
      labelKey: isKnown ? REASON_LABEL_KEYS[code] : UNKNOWN_REASON_LABEL_KEY,
      isKnown,
      blocking: isPlainObject(issue) ? issue.blocking === true : false,
      key: `${code || "unknown"}-${index}`,
    };
  });
}

function buildStatusResult(status, extra) {
  const presentation = STATUS_PRESENTATION[status];
  return {
    applicable: true,
    status,
    severity: presentation.severity,
    blocking: false,
    titleKey: presentation.titleKey,
    descriptionKey: presentation.descriptionKey,
    reasonItems: [],
    validatedAt: null,
    contractVersion: null,
    isFallback: false,
    isPayloadSupported: false,
    lifecycleClassification: null,
    ...extra,
  };
}

// A payload that could not be trusted (absent, null, malformed, wrong shape,
// unsupported version, unknown status). Always degrades to a conservative,
// non-authorizing, non-VALID presentation, with a generic (non-raw-key)
// message describing *why* — never a crash, never a false VALID.
function degradedResult(descriptionKey, extra = {}) {
  return buildStatusResult(OPERATION_HEALTH_STATUS.UNKNOWN, {
    descriptionKey,
    ...extra,
  });
}

function unavailableResult() {
  return {
    applicable: false,
    status: null,
    severity: "neutral",
    blocking: false,
    titleKey: null,
    descriptionKey: "operationHealthErrorUnavailable",
    reasonItems: [],
    validatedAt: null,
    contractVersion: null,
    isFallback: false,
    isPayloadSupported: false,
    lifecycleClassification: null,
  };
}

/**
 * Pure mapping from a raw `operation_health` payload (as attached by the
 * backend read paths) to a stable UI model. Never throws, never re-derives
 * business data, never produces a false VALID.
 *
 * @param {object|null|undefined} operationHealth - the raw `operation.operation_health` value
 * @returns {object} UI-ready presentation
 */
export function presentOperationHealth(operationHealth) {
  if (operationHealth === undefined || operationHealth === null) {
    return unavailableResult();
  }

  if (!isPlainObject(operationHealth)) {
    return degradedResult("operationHealthErrorMalformed", { isMalformed: true });
  }

  const {
    applicable,
    health_status: healthStatus,
    issues,
    validated_at: validatedAtRaw,
    validation_contract_version: contractVersion,
  } = operationHealth;

  if (typeof applicable !== "boolean") {
    return degradedResult("operationHealthErrorMalformed", { isMalformed: true });
  }

  if (typeof contractVersion !== "string" || contractVersion.length === 0) {
    // The verified contract always sets this field (base result included) —
    // its absence means the payload itself is not trustworthy.
    return degradedResult("operationHealthErrorMalformed", { isMalformed: true });
  }

  if (!Array.isArray(issues)) {
    return degradedResult("operationHealthErrorMalformed", { isMalformed: true });
  }

  if (contractVersion !== SUPPORTED_CONTRACT_VERSION) {
    return degradedResult("operationHealthErrorUnsupportedContract", {
      isUnsupportedVersion: true,
      contractVersion,
    });
  }

  const validatedAt = toValidDate(validatedAtRaw);

  if (!applicable) {
    const { lifecycle_classification: lifecycleClassificationRaw } = operationHealth;
    const lifecycleClassification = typeof lifecycleClassificationRaw === "string" ? lifecycleClassificationRaw : null;
    return buildStatusResult(OPERATION_HEALTH_STATUS.NOT_APPLICABLE, {
      applicable: false,
      validatedAt,
      contractVersion,
      isPayloadSupported: true,
      lifecycleClassification,
    });
  }

  if (typeof healthStatus !== "string" || !Object.prototype.hasOwnProperty.call(STATUS_PRESENTATION, healthStatus) || healthStatus === "NOT_APPLICABLE") {
    return degradedResult("operationHealthErrorMalformed", { isMalformed: true });
  }

  const reasonItems = buildReasonItems(issues);
  const blocking = healthStatus === OPERATION_HEALTH_STATUS.BLOCKED || reasonItems.some((item) => item.blocking);

  return buildStatusResult(healthStatus, {
    blocking,
    reasonItems,
    validatedAt,
    contractVersion,
    isPayloadSupported: true,
  });
}

// Confined legacy adapter: represents only what the pre-existing client-side
// check (`isSrcTanksStillInvalid` / `isInvalidSrcTank` in
// src/services/common.js and src/components/OperationsDetail/index.js)
// actually knows — a single boolean about the source tank. It must never be
// promoted to VALID (absence of the one thing it checks is not proof of
// health) and is always explicitly marked `isFallback: true` so callers never
// mistake it for an authoritative backend result.
export function presentLegacyOperationHealthFallback(isInvalidSrcTank) {
  if (isInvalidSrcTank) {
    return buildStatusResult(OPERATION_HEALTH_STATUS.BLOCKED, {
      reasonItems: buildReasonItems([{ code: "SRC_TANK_NOW_EMPTY", blocking: true }]),
      isFallback: true,
    });
  }

  return {
    applicable: false,
    status: null,
    severity: "neutral",
    blocking: false,
    titleKey: null,
    descriptionKey: null,
    reasonItems: [],
    validatedAt: null,
    contractVersion: null,
    isFallback: true,
    isPayloadSupported: false,
    lifecycleClassification: null,
  };
}

/**
 * Resolves the presentation for one operation row: the backend
 * `operation_health` payload is authoritative whenever the key is present at
 * all (even if `null`, which is presented as "unavailable", never silently
 * swapped for the legacy check). The legacy fallback is only reached when the
 * key is entirely absent from the operation object, i.e. a real read path
 * that has not (yet) been migrated to attach `operation_health`.
 *
 * @param {object} operation - the operation row as stored in Redux
 * @param {{isInvalidSrcTank?: boolean}} [legacyInputs]
 */
export function resolveOperationHealthPresentation(operation, legacyInputs = {}) {
  const hasKey = !!operation && Object.prototype.hasOwnProperty.call(operation, "operation_health");
  if (hasKey) {
    return presentOperationHealth(operation.operation_health);
  }
  return presentLegacyOperationHealthFallback(!!legacyInputs.isInvalidSrcTank);
}
